/**
 * SlideFilmstripItem - Individual slide thumbnail in the filmstrip
 *
 * Shows different states:
 * - Pending: Gray gradient with slide number
 * - Generating: violet tint with sparkles and thinking text
 * - Completed: Actual thumbnail image
 * - Failed: Red border with error icon
 *
 * Enhanced with stuck detection:
 * - Shows elapsed time during generation
 * - Shows warning (amber) after 2 minutes
 * - Shows stuck indicator (orange pulse) after 5 minutes
 */

import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Sparkles, Check, AlertCircle, Loader2, Trash2, AlertTriangle } from 'lucide-react';
import type { SlideGenerationStatus } from '@/stores/videoWorkspaceStore';
import { STUCK_THRESHOLDS } from '@/hooks/useStuckSlideDetection';

interface SlideFilmstripItemProps {
  id: string;
  title: string;
  index: number;
  thumbnail?: string;
  duration?: number;
  status: SlideGenerationStatus;
  generatingThinking?: string;
  generationStartedAt?: string; // ISO timestamp for elapsed time display
  isStuck?: boolean;            // Backend flag indicating potential stuck state
  isActive: boolean;
  onClick: () => void;
  conversationId?: string; // Used for cache-busting to prevent cross-conversation image contamination
  onDelete?: () => void; // Optional delete handler - shows delete button on hover
}

export const SlideFilmstripItem: React.FC<SlideFilmstripItemProps> = ({
  id,
  title,
  index,
  thumbnail,
  duration = 5,
  status,
  generatingThinking,
  generationStartedAt,
  isStuck: backendIsStuck,
  isActive,
  onClick,
  conversationId,
  onDelete,
}) => {
  const isPending = status === 'pending';
  const isGenerating = status === 'generating';
  const isFailed = status === 'failed';
  const isCompleted = status === 'completed';

  // Track elapsed time for generating slides
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (!isGenerating || !generationStartedAt) {
      setElapsedMs(0);
      return;
    }

    // Calculate initial elapsed time
    const startedAt = new Date(generationStartedAt).getTime();
    setElapsedMs(Date.now() - startedAt);

    // Update every second
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 1000);

    return () => clearInterval(interval);
  }, [isGenerating, generationStartedAt]);

  // Determine stuck status based on elapsed time or backend flag
  const isWarning = elapsedMs >= STUCK_THRESHOLDS.WARNING && elapsedMs < STUCK_THRESHOLDS.STUCK;
  const isStuck = elapsedMs >= STUCK_THRESHOLDS.STUCK || backendIsStuck;

  // Format elapsed time for display
  const formatElapsed = (ms: number): string => {
    if (ms < 1000) return '0s';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes === 0) return `${seconds}s`;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  // Cache-bust thumbnail URL with conversationId to prevent cross-conversation image contamination
  // This ensures browser doesn't serve stale cached images when switching between videos
  // Skip for S3 pre-signed URLs — appending params invalidates the v4 signature,
  // and the signed URL's timestamp already provides natural cache-busting
  const cacheBustedThumbnail = React.useMemo(() => {
    if (!thumbnail) return undefined;
    if (thumbnail.includes('X-Amz-Signature')) return thumbnail;
    const separator = thumbnail.includes('?') ? '&' : '?';
    return `${thumbnail}${separator}conv=${conversationId || 'default'}`;
  }, [thumbnail, conversationId]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering slide selection
    onDelete?.();
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        'group relative w-24 h-14 rounded-lg overflow-hidden cursor-pointer transition-all',
        'border-2 flex-shrink-0',
        isActive
          ? 'border-primary ring-2 ring-primary/30'
          : 'border-transparent hover:border-zinc-700',
        isPending && 'opacity-60',
        isFailed && 'border-red-500',
        // Stuck/warning styling for generating slides
        isGenerating && isStuck && 'border-orange-500 animate-pulse',
        isGenerating && isWarning && !isStuck && 'border-amber-500'
      )}
    >
      {/* Background: thumbnail, gradient, or placeholder */}
      {isCompleted && thumbnail ? (
        <img
          key={`${conversationId}-${id}`} // Force recreation when conversation changes
          src={cacheBustedThumbnail}
          alt={title}
          className="w-full h-full object-cover"
        />
      ) : (
        <div
          className={cn(
            'w-full h-full flex items-center justify-center',
            isPending && 'bg-gradient-to-br from-zinc-800 to-zinc-900',
            isGenerating && 'bg-primary/15',
            isFailed && 'bg-gradient-to-br from-red-900/30 to-zinc-900',
            isCompleted && !thumbnail && 'bg-gradient-to-br from-zinc-800 to-zinc-900'
          )}
        >
          {/* Pending state */}
          {isPending && (
            <div className="text-center">
              <div className="text-xs text-zinc-500 font-medium">
                {index + 1}
              </div>
              <div className="text-[8px] text-zinc-600 truncate px-1">
                {title}
              </div>
            </div>
          )}

          {/* Generating state with thinking and elapsed time */}
          {isGenerating && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-1">
              {/* Stuck warning icon for > 5 minutes */}
              {isStuck ? (
                <div className="relative">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                </div>
              ) : (
                /* Normal pulsing animation ring */
                <div className="relative">
                  <Sparkles className={cn(
                    'w-4 h-4 animate-pulse',
                    isWarning ? 'text-amber-400' : 'text-primary'
                  )} />
                  <div className="absolute inset-0 animate-ping opacity-30">
                    <Sparkles className={cn(
                      'w-4 h-4',
                      isWarning ? 'text-amber-400' : 'text-primary'
                    )} />
                  </div>
                </div>
              )}

              {/* Elapsed time display */}
              {generationStartedAt && (
                <div className={cn(
                  "mt-0.5 text-[7px] font-medium",
                  isStuck ? 'text-orange-300' : isWarning ? 'text-amber-300' : 'text-primary'
                )}>
                  {formatElapsed(elapsedMs)}
                </div>
              )}

              {/* Stuck message or thinking text */}
              <div className={cn(
                "text-[7px] text-center line-clamp-2 px-1",
                isStuck ? 'text-orange-300' : isWarning ? 'text-amber-300' : 'text-primary'
              )}>
                {isStuck
                  ? 'May be stuck'
                  : isWarning
                    ? 'Taking a while...'
                    : generatingThinking || 'Processing...'}
              </div>

              {/* Progress dots - hide when stuck */}
              {!isStuck && (
                <div className="flex gap-0.5 mt-0.5">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className={cn(
                        'w-1 h-1 rounded-full animate-bounce',
                        isWarning ? 'bg-amber-400' : 'bg-primary'
                      )}
                      style={{ animationDelay: `${i * 150}ms` }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Failed state */}
          {isFailed && (
            <div className="text-center">
              <AlertCircle className="w-4 h-4 text-red-400 mx-auto" />
              <div className="text-[8px] text-red-400 mt-0.5">Failed</div>
            </div>
          )}

          {/* Completed but no thumbnail */}
          {isCompleted && !thumbnail && (
            <div className="text-center">
              <Check className="w-4 h-4 text-emerald-400 mx-auto" />
              <div className="text-[8px] text-zinc-400 mt-0.5">{index + 1}</div>
            </div>
          )}
        </div>
      )}

      {/* Slide number badge */}
      <div className="absolute top-1 left-1 bg-black/60 rounded px-1 text-[8px] text-white">
        {index + 1}
      </div>

      {/* Duration badge (only for completed) */}
      {isCompleted && (
        <div className="absolute bottom-1 right-1 bg-black/60 rounded px-1 text-[8px] text-white">
          {duration}s
        </div>
      )}

      {/* Generating indicator */}
      {isGenerating && (
        <div className="absolute top-1 right-1">
          <Loader2 className="w-3 h-3 text-primary animate-spin" />
        </div>
      )}

      {/* Delete button - appears on hover when onDelete is provided */}
      {onDelete && !isGenerating && (
        <button
          onClick={handleDeleteClick}
          className={cn(
            'absolute top-1 right-1 p-0.5 rounded bg-red-500/80 text-white',
            'opacity-0 group-hover:opacity-100 transition-opacity',
            'hover:bg-red-600'
          )}
          title="Delete slide"
        >
          <Trash2 className="w-2.5 h-2.5" />
        </button>
      )}

    </div>
  );
};

export default SlideFilmstripItem;
